import { Injectable, Inject, Injector } from '@angular/core';
import { Http } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import _ from 'lodash';

import { AuthService } from '../../auth/auth.service';
import { AppConfigurationService } from '../../common/app-configuration.service';
import { EventsManagerService } from '../../common/events-manager.service';
import { HttpInterceptor } from '../../common/http-interceptor.service';
import { DatabaseService } from '../../storage/database.service';
import { ResourceReaderService } from '../resource-reader.service';
import i18nResource from './i18n-resource';

@Injectable()
export class SmartenitTranslationService extends ResourceReaderService {
  public static readonly I18N_KEY = 'i18n_data';
  public static readonly LANGUAGES = {
    ENGLISH: 'en',
    PORTUGUESE: 'pt',
    SPANISH: 'es'
  };

  private language: string = SmartenitTranslationService.LANGUAGES.ENGLISH;

  constructor(
    http: HttpInterceptor,
    authService: AuthService,
    databaseService: DatabaseService,
    eventsService: EventsManagerService,
    AppConfiguration: AppConfigurationService,
    injector: Injector
  ) {
    super(
      'resources',
      http,
      authService,
      databaseService,
      eventsService,
      AppConfiguration,
      injector,
      SmartenitTranslationService.I18N_KEY,
      'I18N resources'
    );
  }

  public loadResources() {
    this.loadLocalVersion().subscribe((localVersionResponse) => {
      const date = new Date();
      const cacheString =
        '' + date.getUTCDate() + date.getUTCHours() + date.getUTCMinutes();
      this.get('i18n', { v: cacheString }).subscribe(
        (i18nData) => {
          if (i18nData) {
            this.storeLocalVersion(i18nData);
          }
          this._loaded = true;
          this._onLoaded.next(true);
        },
        (error) => {
          this._loaded = true;
          this._onLoaded.next(true);
          console.log(
            'Error loading i18n resources, using local version (' +
              this.version +
              ')'
          );
        }
      );
    });
  }

  protected buildInitialData(): Observable<any> {
    return this.databaseService
      .getCollection(SmartenitTranslationService.I18N_KEY)
      .save(SmartenitTranslationService.I18N_KEY, i18nResource)
      .map(() => {
        this._data = i18nResource;
      });
  }

  protected processData(remoteData: any) {
    this._data = remoteData;
  }

  /**
   * Set the language for translations
   * @param value New language
   */
  public setLanguage(value: string) {
    this.language =
      value &&
      _.values(SmartenitTranslationService.LANGUAGES).indexOf(value) > -1
        ? value
        : SmartenitTranslationService.LANGUAGES.ENGLISH;
  }

  /**
   * Translate a text based on a key to the desired language
   * @param key Key for the text
   */
  public translate(key: string): Observable<string> {
    if (this.isLoaded) {
      return Observable.of(this.performTranslation(key));
    } else {
      return this.onLoaded.map((isLoaded) => {
        return this.performTranslation(key);
      });
    }
  }

  /**
   * Translate multiple texts based on their keys to the desired language
   * @param keys Keys for the texts
   */
  public multiTranslate(
    keys: string[]
  ): Observable<{ [key: string]: string }> {
    const translateKeys = (tkeys: string[]): { [key: string]: string } =>
      tkeys.reduce((obj: { [key: string]: string }, key: string) => {
        obj[key] = this.performTranslation(key);
        return obj;
      }, {});

    if (this.isLoaded) {
      return Observable.of(translateKeys(keys));
    } else {
      return this.onLoaded.map((isLoaded) => {
        return translateKeys(keys);
      });
    }
  }

  /**
   * Translate a text based on a key to the desired language
   * @param key Key for the text
   */
  public performTranslation(key: string): string {
    return _.get(this.getResource(), key, '');
  }

  /**
   * Retrieves the current translation resource
   */
  public getResource(lang: string = this.language): any {
    let language;

    switch (lang) {
      default:
      case SmartenitTranslationService.LANGUAGES.ENGLISH:
        language = SmartenitTranslationService.LANGUAGES.ENGLISH;
        break;

      case SmartenitTranslationService.LANGUAGES.SPANISH:
        language = SmartenitTranslationService.LANGUAGES.SPANISH;
        break;

      case SmartenitTranslationService.LANGUAGES.PORTUGUESE:
        language = SmartenitTranslationService.LANGUAGES.PORTUGUESE;
        break;
    }

    return _.get(this.data, language, '');
  }

  /**
   * Reorder the words in a sentences based on the language
   * @param subject Subject of the sentence
   * @param adjective Adjective for the subject
   */
  public reorderSentence(subject: string, adjective: string) {
    switch (this.language) {
      default:
      case SmartenitTranslationService.LANGUAGES.ENGLISH:
        return `${subject} ${adjective}`;

      case SmartenitTranslationService.LANGUAGES.SPANISH:
        return `${adjective} ${subject}`;
    }
  }
}
